import React from 'react';
import gql from 'graphql-tag';
import { RCAlertBox } from '@reverbdotcom/commons/src/rc/RCAlertBox';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { Paths } from '@reverbdotcom/commons/src/url_helpers';
import { RCTextWithIcon } from '@reverbdotcom/commons/src/rc/RCTextWithIcon';
import SearchAltIcon from '@reverbdotcom/commons/design-system/icons/core/search-alt.svg?component';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';
import { ChildProps } from 'react-apollo';
import withSuggestionCheck, { isSuggestionValid } from './with_suggestion_check';
import { CoreWithSuggestionCheckListingsSearch } from '@reverbdotcom/commons/src/rql';

interface IExternalProps {
  query: string;
  suggestion: string;
}

type IProps = ChildProps<IExternalProps, CoreWithSuggestionCheckListingsSearch.Query>;

const COMPONENT_NAME = 'NoResultsAlert';

export const MarketplaceSuggestionsFragment = gql`
  fragment MarketplaceSuggestions on reverb_search_SearchResponse_Suggestion {
    text
  }
`;

export function NoResultsAlert(props: IProps) {
  const viewTrackingData = {
    componentName: COMPONENT_NAME,
    eventName: MParticleEventName.ComponentView,
    suggestion: props.suggestion,
  };

  useViewTracking(viewTrackingData, !!props.query);

  if (!props.query) return null;

  const clickTrackingData = {
    componentName: 'NoResultsAlert',
    eventName: MParticleEventName.ClickedSearchSuggestion,
    suggestion: props.suggestion,
  };

  const suggestionValid = isSuggestionValid(props);

  return (
    <div className="mb-3">
      <RCAlertBox
        type="error"
        headerText={suggestionValid && <NoResultsFor query={props.query} />}
      >
        {suggestionValid ? (
          <div className="d-flex fx-align-center fx-wrap">
            <I18N html text="discovery.noResults.didYouMean" />
            <CoreLink
              className="button mlr-space mobile-mlr-0 mobile-d-block mobile-mt-1 ws-normal"
              to={Paths.marketplace.expand({ query: props.suggestion })}
              clickEvent={clickTrackingData}
            >
              <RCTextWithIcon svgComponent={SearchAltIcon}>
                <span>{props.suggestion}</span>
              </RCTextWithIcon>
            </CoreLink>
          </div>
        ) : (
          <NoResultsFor query={props.query} />
        )}
      </RCAlertBox>
    </div>
  );
}

function NoResultsFor({ query }) {
  return (
    <I18N
      html
      text="discovery.noResults.showingNoResultsFor"
    >
      <span className="weight-bold"><q>{query}</q></span>
    </I18N>
  );
}

export default withSuggestionCheck<IProps>(NoResultsAlert);
